/**
 * Заявки компаний.
 *
 * Публичной регистрации у кабинета нет: за каждым брендом стоит
 * договорённость с живым человеком. Этот экран — начало такой
 * договорённости, а не очередь на автоматическое одобрение.
 *
 * Отказ отмечается наравне с приглашением. Заявка, пропавшая без следа,
 * выглядит для компании так же, как «нас проигнорировали», а для платформы —
 * как необработанная: через месяц никто не вспомнит, смотрели её или нет.
 */
import type { CompanyRequest } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, REGIONS, Segmented, Skeleton, fmtShortDay } from '@allswipes/ui';
import { useState } from 'react';

import { api } from '../api';
import { Layout, ReadOnlyNote } from '../components/Layout';
import { useResource } from '../hooks';
import { useAdmin } from '../session';
import { useToast } from '../toast';

const STATE: Record<string, { label: string; tone?: 'good' | 'bad' }> = {
  new: { label: 'новая' },
  invited: { label: 'приглашена', tone: 'good' },
  declined: { label: 'отказ', tone: 'bad' },
};

export function CompanyRequestsScreen() {
  const { canModerate } = useAdmin();
  const toast = useToast();
  const [filter, setFilter] = useState('new');
  const list = useResource(() => api.adminCompanyRequests(filter || undefined), [filter]);

  const handle = async (req: CompanyRequest, status: string) => {
    try {
      await api.adminHandleCompanyRequest(req.id, status);
      toast(status === 'declined' ? 'Отмечено: отказ' : 'Отмечено');
      list.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  const regionName = (code?: string) =>
    REGIONS.find((r) => r.code === code)?.name ?? code ?? '—';

  return (
    <Layout title="Заявки компаний" subtitle="кто просится проводить акции">
      {!canModerate && <ReadOnlyNote />}

      <div style={{ maxWidth: 420, marginBottom: 18 }}>
        <Segmented
          value={filter}
          options={[
            { value: 'new', label: 'Новые' },
            { value: 'invited', label: 'Приглашены' },
            { value: 'declined', label: 'Отказ' },
            { value: '', label: 'Все' },
          ]}
          onChange={setFilter}
        />
      </div>

      {list.loading && <Skeleton count={3} />}

      {list.data && list.data.requests.length === 0 && (
        <Empty title="Заявок нет">
          Форма живёт по адресу /for-companies в приложении.
        </Empty>
      )}

      {list.data?.requests.map((req) => (
        <Card key={req.id} style={{ marginBottom: 12 }}>
          <div className="row" style={{ marginBottom: 8 }}>
            <strong>{req.company}</strong>
            <span className="spacer" />
            <Badge tone={STATE[req.status]?.tone}>{STATE[req.status]?.label ?? req.status}</Badge>
          </div>

          <div className="sub" style={{ marginBottom: 4 }}>
            {[req.name, req.surname].filter(Boolean).join(' ')} · {regionName(req.region)}
          </div>
          <div className="sub mono" style={{ fontSize: 12, marginBottom: 8 }}>
            {req.email}
            {req.phone ? ` · ${req.phone}` : ''}
          </div>

          {req.about && (
            <p className="sub" style={{ marginBottom: 10 }}>
              {req.about}
            </p>
          )}

          <div className="row" style={{ gap: 8 }}>
            <span className="faint" style={{ fontSize: 12 }}>
              {fmtShortDay(req.created_at)}
              {req.handled_by ? ` · ${req.handled_by}` : ''}
            </span>
            <span className="spacer" />
            {canModerate && req.status === 'new' && (
              <>
                <Button size="sm" onClick={() => void handle(req, 'invited')}>
                  Завели и пригласили
                </Button>
                <Button size="sm" variant="danger" onClick={() => void handle(req, 'declined')}>
                  Отказ
                </Button>
              </>
            )}
            {canModerate && req.status !== 'new' && (
              <Button size="sm" onClick={() => void handle(req, 'new')}>
                Вернуть в новые
              </Button>
            )}
          </div>
        </Card>
      ))}
    </Layout>
  );
}
